/**
 * canonicalizeFormDeriveValue
 *
 * The one value-canonicalization rule of the derive engine, shared by the
 * `colUniques` grouping key and the fingerprint material; `undefined` means
 * blank. Its output is PERSISTED inside projected row ids and stamp hashes, so
 * every spelling is frozen. References canonicalize by `docId` alone, so two
 * `cacheLabel` snapshots of one record collapse to one key, and Timestamps are
 * spelled `ts:` at EVERY depth — never enumerated, because their own keys differ
 * between the client and admin SDKs and would fork the hash across processes.
 */

import { isManualEntryValue } from '../System.Artifacts.types';
import { isRecordValue } from './isRecordValue';
import { isTimestampShaped } from './isTimestampShaped';

/**
 * Any `ArReferenceValue` arm — entity, form OR artifact. Deliberately wider than
 * the exported `isEntityReference`: a uniques key groups by the referenced
 * document, and which kind of document it is does not change that identity.
 */
function isAnyReferenceValue(value: unknown): value is { referenceType: unknown; docId: string } {
  return (
    isRecordValue(value) &&
    value.referenceType !== undefined &&
    typeof value.docId === 'string'
  );
}

/** The canonical Timestamp spelling, identical at the top level and at any depth. */
function canonicalizeTimestamp(value: { seconds: number; nanoseconds: number }): string {
  return `ts:${value.seconds}.${value.nanoseconds}`;
}

/** True when a manual entry carries a composite payload rather than a scalar. */
function hasCompositePayload(value: Record<string, unknown>): boolean {
  const composite = [value.fields, value.entityReferences];
  return composite.some((part) => isRecordValue(part) && Object.keys(part).length > 0);
}

/**
 * Recursively rebuilds the value with object keys sorted; arrays keep their
 * order. A nested Timestamp or reference collapses to its scalar spelling BEFORE
 * the walk can enumerate it: a Timestamp's own keys differ between SDKs, and a
 * reference carries a `cacheLabel` snapshot that moves without its identity
 * moving. Enumerating either would fork the canon of unchanged data.
 */
function sortKeysDeep(value: unknown): unknown {
  if (typeof value === 'bigint') return String(value);
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (isTimestampShaped(value)) return canonicalizeTimestamp(value);
  if (isAnyReferenceValue(value)) return `ref:${value.docId}`;
  if (isRecordValue(value)) {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, sortKeysDeep(value[key])]),
    );
  }
  return value;
}

function canonicalizeFormDeriveValue(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : `str:${trimmed}`;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? `num:${String(value)}` : undefined;
  }
  if (typeof value === 'boolean') return value ? 'bool:true' : 'bool:false';
  if (isAnyReferenceValue(value)) return `ref:${value.docId}`;
  if (isManualEntryValue(value)) {
    if (typeof value.value === 'string' && value.value.trim() !== '') {
      return canonicalizeFormDeriveValue(value.value);
    }
    if (!hasCompositePayload(value)) return undefined;
  }
  if (isTimestampShaped(value)) return canonicalizeTimestamp(value);
  if (typeof value === 'object') return `obj:${JSON.stringify(sortKeysDeep(value))}`;
  return `obj:${String(value)}`;
}

export { canonicalizeFormDeriveValue };
